import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as translator from '../../../../utils/translator';
import { CustomerAddressCreation, createCustomerAddressCreationSchema } from '../MyAccountDeliveryAddressV2/schemas';
import RestrictedInputField from '../../../../components/CredentialsInput/RestrictedInputField';
import InputField from '../../../../components/CredentialsInput/InputField';
import GoogleApiRestrictedInputField from '../../../../components/CredentialsInput/GoogleApiRestrictedInputField';
import SubmitButton from '../../../../components/SubmitButton/SubmitButton';
import Lock from '../../../../icons/Lock.svg';

const FORM_ID = 'change-address-form';

type MyAccountChangeAddressFormProps = {
  onSubmit: (formData: CustomerAddressCreation) => void;
  language: string;
  country: string;
  defaultFormValues: CustomerAddressCreation;
  countryOptions: { id: string; value: string }[];
  isGoogleApiLoaded: boolean;
  isSubmitting: boolean;
  submitButtonLabel: string;
};

const MyAccountChangeAddressForm = ({
  onSubmit,
  language,
  country,
  defaultFormValues,
  countryOptions,
  isGoogleApiLoaded,
  isSubmitting,
  submitButtonLabel,
}: MyAccountChangeAddressFormProps) => {
  const customerAddressSchema = createCustomerAddressCreationSchema(language);
  const { handleSubmit, reset, control } = useForm<CustomerAddressCreation>({
    resolver: zodResolver(customerAddressSchema),
    mode: 'onTouched',
    defaultValues: defaultFormValues,
  });

  const [selectedCountry] = useState<{ id: string; value: string } | undefined>(
    countryOptions.find(opt => opt.id === defaultFormValues.country_id?.toLowerCase()) || countryOptions[0]
  );

  useEffect(() => {
    reset(defaultFormValues);
  }, [defaultFormValues, reset]);

  const isCountryLocked = countryOptions.length === 1;
  const countryLabel = selectedCountry?.value || translator.translate(language, 'shipping', 'ShippingForm/country');

  return (
    <div id="address-modal" className="[&_input]:border-solid [&_input[type=button]]:border-[#11DDAC] max-w-md w-full rounded-md rounded-tl-3xl rounded-br-3xl address-form-floating-labels">
      <form id={FORM_ID} className="flex flex-col gap-y-4 w-full max-w-md mx-auto" onSubmit={handleSubmit(onSubmit)}>
        <div id="address-form-container" className="flex flex-col gap-y-4 px-0 md:px-4">
          <div id="address-name-row" className="grid grid-cols-1 md:grid-cols-2 gap-y-4 md:gap-y-0 md:gap-x-4">
            <Controller name="first_name" control={control} render={({ field, fieldState }) => (
              <RestrictedInputField id="address-firstname" value={field.value} onChange={field.onChange} onBlur={field.onBlur}
                placeholder={translator.translate(language, 'shipping', 'ShippingForm/first_name')} mandatory={true} language={language}
                ignoreTouched={true} errorMsg={fieldState.error?.message ?? ''} />
            )} />
            <Controller name="last_name" control={control} render={({ field, fieldState }) => (
              <RestrictedInputField id="address-lastname" value={field.value} onChange={field.onChange} onBlur={field.onBlur}
                placeholder={translator.translate(language, 'shipping', 'ShippingForm/surname')} mandatory={true} language={language}
                ignoreTouched={true} errorMsg={fieldState.error?.message ?? ''} />
            )} />
          </div>
          <div id="address-location-row" className="grid grid-cols-1 md:grid-cols-2 gap-y-3 md:gap-y-0 md:gap-x-4">
            <Controller name="zip" control={control} render={({ field, fieldState }) => (
              <RestrictedInputField id="address-zip" value={field.value} onChange={field.onChange} onBlur={field.onBlur}
                placeholder={translator.translate(language, 'shipping', 'ShippingForm/postal_code')} mandatory={true} language={language}
                ignoreTouched={true} errorMsg={fieldState.error?.message ?? ''} />
            )} />
            <Controller name="city" control={control} render={({ field, fieldState }) => (
              <GoogleApiRestrictedInputField id="address-city" value={field.value}
                onChange={(value: string) => field.onChange(value.split(',')[0])}
                onBlur={field.onBlur}
                placeholder={translator.translate(language, 'shipping', 'ShippingForm/location')} mandatory={true}
                country={country} addressType="(cities)" isGoogleApiLoaded={isGoogleApiLoaded}
                errorMsg={fieldState.error?.message ?? ''} />
            )} />
          </div>
          <div id="address-street-container">
            <Controller name="address" control={control} render={({ field, fieldState }) => (
              <GoogleApiRestrictedInputField id="address-street" value={field.value}
                onChange={(value: string) => field.onChange(value.split(',')[0])}
                onBlur={field.onBlur}
                placeholder={translator.translate(language, 'shipping', 'ShippingForm/street_and_house')} mandatory={true}
                country={country} addressType="address" isGoogleApiLoaded={isGoogleApiLoaded}
                errorMsg={fieldState.error?.message ?? ''} />
            )} />
          </div>
          <div id="address-country-company-row" className="grid grid-cols-1 md:grid-cols-2 gap-y-4 md:gap-y-0 md:gap-x-4">
            <div id="address-country-container">
              {isCountryLocked ? (
                <InputField id="address-country" value={countryLabel}
                  placeholder={translator.translate(language, 'my_account', 'MyAccountChangeAddress/country')}
                  mandatory={false} disabled={true} language={language} onChange={() => {}}
                  childrenIcon={<img src={Lock} alt="lock" />} />
              ) : null}
            </div>
            <div id="address-company-container">
              <Controller name="company" control={control} render={({ field, fieldState }) => (
                <RestrictedInputField id="address-company" value={field.value ?? ''} onChange={field.onChange} onBlur={field.onBlur}
                  placeholder={translator.translate(language, 'my_account', 'DeliveryAddressV2/company')} mandatory={false} language={language}
                  ignoreTouched={true} errorMsg={fieldState.error?.message ?? ''} />
              )} />
            </div>
          </div>
          <SubmitButton id="address-form-submit-button" formID={FORM_ID} placeholder={submitButtonLabel}
            loading={isSubmitting} language={language} width="100%" />
        </div>
      </form>
    </div>
  );
};

export default MyAccountChangeAddressForm;
